import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const megaMenuItems = [
    { title: "Guest Posts", desc: "Build authority with quality content.", icon: FiIcons.FiEdit3, link: "/services/guest-posts" },
    { title: "Press Releases", desc: "Gain massive media coverage.", icon: FiIcons.FiMic, link: "/services/press-releases" },
    { title: "Link Insertions", desc: "Placements on authority content.", icon: FiIcons.FiLink2, link: "/services/link-insertions" },
    { title: "Reddit", desc: "Drive real community discussions.", icon: FaIcons.FaReddit, link: "/services/reddit" },
    { title: "Quora", desc: "Capture long-tail search traffic.", icon: FaIcons.FaQuora, link: "/services/quora" },
    { title: "YouTube SEO", desc: "Boost your video presence.", icon: FaIcons.FaYoutube, link: "/services/youtube" },
    { title: "Google Maps", desc: "Rank #1 for local businesses.", icon: FaIcons.FaGoogle, link: "/services/google-maps" },
    { title: "Brand Mentions", desc: "Get trusted and seen.", icon: FiIcons.FiAward, link: "/services/brand-mentions" }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-[#0B0F19]/95 backdrop-blur-xl border-b border-white/5 py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group z-[110]">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/57fe1cdd9b663cb2c02a7dabf87358cb277fe0e0f347cf6e941ee58c79db65ed65994d3a49b6ee312e2fb668c3d742bb/images/aW1hZ2UvcG5n/a47a3338f592a3c249f60f7c6ec25b30.png" 
              alt="PRBoost Logo" 
              className="w-10 h-10 rounded-lg object-contain group-hover:scale-105 transition-transform" 
            />
            <span className="text-xl font-bold tracking-tight text-white">PRBoost</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <div className="relative group/nav h-full flex items-center">
              <button className="flex items-center gap-1 text-sm font-medium text-slate-300 group-hover/nav:text-white transition-colors py-2">
                Services <SafeIcon icon={FiIcons.FiChevronDown} className="text-xs transition-transform duration-300 group-hover/nav:rotate-180" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover/nav:opacity-100 group-hover/nav:visible transition-all duration-300 w-[700px] z-[100]">
                <div className="bg-[#0D111C] border border-white/10 rounded-2xl shadow-2xl p-8 grid grid-cols-2 gap-6">
                  {megaMenuItems.map((item, idx) => (
                    <Link key={idx} to={item.link} className="flex items-start gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors group/item">
                      <div className="text-blue-400 mt-1"><SafeIcon icon={item.icon} className="text-xl" /></div>
                      <div>
                        <div className="text-sm font-bold text-white group-hover/item:text-blue-400 transition-colors">{item.title}</div>
                        <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link to="/contact" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Contact</Link>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <Button href="https://app.prboost.io/" variant="ghost" className="!px-4 !text-sm">Login</Button>
            <Button href="https://app.prboost.io/" variant="primary" className="!px-5 !py-2.5 !text-sm">To the Platform</Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden text-white z-[110] p-2 focus:outline-none" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-2xl" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Moved outside of the NAV to avoid backdrop-filter issues */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 bg-[#0B0F19] z-[98] pt-28 px-6 overflow-y-auto pb-10"
          >
            <div className="flex flex-col space-y-6">
              <div className="space-y-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest pl-2">Our Services</p>
                <div className="grid grid-cols-1 gap-3">
                  {megaMenuItems.map((item, idx) => (
                    <Link 
                      key={idx} 
                      to={item.link} 
                      className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors"
                    >
                      <SafeIcon icon={item.icon} className="text-xl text-blue-400" />
                      <span className="text-sm font-bold text-white">{item.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
              
              <div className="h-[1px] bg-white/5 w-full my-4" />
              
              <div className="flex flex-col space-y-4 pl-2">
                <Link to="/contact" className="text-lg font-bold text-white">Contact Us</Link>
              </div>
              
              <div className="pt-6 flex flex-col gap-4">
                <Button href="https://app.prboost.io/" variant="primary" className="w-full !py-4 text-base">To the Platform</Button>
                <Button href="https://app.prboost.io/" variant="secondary" className="w-full !py-4 text-base">Login to Dashboard</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
